'use client';

import { IconTrash } from '@tabler/icons-react';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/spinner';
import type { CartItem as CartItemType } from '@/features/cart/types';
import { useCheckoutMutation } from '@/features/orders/hooks/use-checkout-mutation';
import { SellerInfo } from '@/features/store/components/seller-info';
import { cn } from '@/lib/cn';
import { calculateTotalPrice } from '@/lib/utils/cart';
import { useDeleteCartItem } from '../hooks/use-delete-cart-item';
interface CartItemProps {
  seller: string;
  items: CartItemType[];
  cartPage?: boolean;
}
export const CartItem = ({
  items,
  seller,
  cartPage = false
}: CartItemProps) => {
  const {
    mutateAsync: createCheckoutSession,
    isPending
  } = useCheckoutMutation();
  const {
    priceSubtotal,
    priceTotal,
    shippingSubtotal
  } = calculateTotalPrice(items);
  const handleCheckout = useCallback(async () => {
    await createCheckoutSession({
      products: items.map(item => ({
        productId: item.product.id,
        variantId: item.variant?.id
      }))
    });
  }, [createCheckoutSession, items]);
  return <div className={cn('w-full rounded-lg inset-1 p-5 flex shadow-md flex-col gap-4', {
    'flex-row border': cartPage
  })} data-sentry-component="CartItem" data-sentry-source-file="cart-item.tsx">
      {!cartPage && <SellerInfo seller={{
      slug: seller
    }} />}
      <div className="flex-1 space-y-6">
        {items.map(item => {
        return <ProductItem key={item.id} item={item} />;
      })}
      </div>
      <div className="flex flex-col flex-1 space-y-3 mt-3 md:mt-0 lg:mt-0">
        {cartPage && <SellerInfo seller={{
        slug: seller
      }} />}
        <div className={cn('flex justify-between pt-3', {
        'border-t border-gray-200': cartPage
      })}>
          <p className="text-md text-gray-600">Item(s)</p>
          <p>${priceSubtotal}</p>
        </div>
        <div className="flex justify-between">
          <p className="text-md text-gray-600">Shipping</p>
          <p>${shippingSubtotal}</p>
        </div>
        <div className="flex justify-between font-bold">
          <p className="text-md text-gray-900">Total</p>
          <p>${priceTotal.toFixed(2)}</p>
        </div>
        <div />
        <Button loading={isPending} disabled={isPending} onClick={handleCheckout} data-sentry-element="Button" data-sentry-source-file="cart-item.tsx">
          Checkout {items.length} {items.length === 1 ? 'item' : 'items'}
        </Button>
      </div>
    </div>;
};
const ProductItem = ({
  item
}: {
  item: CartItemType;
}) => {
  const {
    mutateAsync: deleteItem,
    isPending
  } = useDeleteCartItem();
  const listing = item.product;
  const title = item.variant?.name ?? listing.name;
  return <div className="flex gap-3" data-sentry-component="ProductItem" data-sentry-source-file="cart-item.tsx">
      <Link href={`/listings/${listing.slug}`} data-sentry-element="Link" data-sentry-source-file="cart-item.tsx">
        <Image className="h-28 w-28 bg-gray-200 object-cover object-center" src={listing.images?.[0]?.url} alt={listing.name} width={112} height={112} data-sentry-element="Image" data-sentry-source-file="cart-item.tsx" />
      </Link>
      <div className="flex flex-col gap-1">
        {item.measurements && <span className="w-fit inline-flex items-center uppercase rounded-md bg-indigo-50 px-2 py-1 text-[10px] font-bold text-primary ring-1 ring-inset ring-primary/10 gap-1">
            Tailoring approved
          </span>}
        <p>{title}</p>
        <span className="flex gap-2 items-center">
          <p className="font-bold">$ {item.price ?? listing.price}</p>
          {item.price && <span className="w-fit inline-flex items-center uppercase rounded-md bg-indigo-50 px-2 py-1 text-[10px] font-bold text-primary ring-1 ring-inset ring-primary/10 gap-1">
              accepted offer price
            </span>}
        </span>
        {isPending ? <Spinner color="black" /> : <button className="w-fit outline-offset-2 text-xs flex items-center gap-1 mt-1" onClick={() => deleteItem(item.id)}>
            <IconTrash className="h-5 w-5" />
          </button>}
      </div>
    </div>;
};